import React, { useEffect } from "react";
import {
  Property,
  PropertyValue,
} from "../../BuildingProductEditor/ProductEditorStore";
import { IconGripVertical, IconTrash } from "@tabler/icons-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import clsx from "clsx";
import PropertyIcon from "../components/PropertyIcon";
import Input from "components/Input";
import { useTranslation } from "react-i18next";
import Checkbox from "components/Checkbox";
import MultiSelect from "../components/MultiSelect";
import Select from "../components/Select";
import ConfirmModal from "components/ConfirmModal";
import { PropertyTypeChoices } from "api-client";
import { propertyManagerStore } from "../PropertyManagerStore";
import { observer } from "mobx-react-lite";
import TextArea from "components/TextArea";

const PropertyRowEditable = ({
  property,
  ...props
}: {
  property: Property;
  index: number;
  sortableId?: string;
  setTitle: (title: string | undefined) => void;
  setValue: (value: PropertyValue) => void;
  onChangeName: () => void;
  removeProperty: (id?: string, slug?: string) => void;
}) => {
  const { t } = useTranslation();
  const id = String(props.sortableId);
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const nameInputRef = React.useRef<HTMLTextAreaElement>(null);
  const [namehasError, setNamehasError] = React.useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
    borderBottom: isDragging ? "1px solid #d1d5db" : "none",
    borderRight: isDragging ? "1px solid #d1d5db" : "none",
    borderLeft: isDragging ? "1px solid #d1d5db" : "none",
    backgroundColor: isDragging ? "#fff" : "transparent",
  };

  const dragHandle = (
    <div
      className={clsx(
        "items-center justify-center cursor-grab bg-white h-full w-full",
        "hidden group-hover/property:flex absolute left-0 top-0 pl-0 pt-0 rounded-md hover:h-full"
      )}
      {...listeners}
      {...attributes}
    >
      <IconGripVertical
        size={25}
        className={clsx("text-indigo-700 !cursor-grab")}
      />
    </div>
  );

  const onChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.setValue(e.currentTarget.checked);
  };

  const onChangeSelect = (value: PropertyValue) => {
    props.setValue(value);
  };

  const onBlurChangeName = (value: string) => {
    setNamehasError(value === "");
    props.onChangeName();
  };

  function propertyName() {
    return (
      <TextArea
        className={clsx(
          "text-gray-900 px-2 py-[8px] min-w-full border-none rounded-none !min-h-full resize-none",
          "flex items-start rounded-none",
          "focus:!shadow-[inset_0_0_0_1px] focus:!shadow-indigo-600 focus:!border-none focus:ring-0"
        )}
        containerClass="w-full h-full"
        placeholder={t("productEditor.nameTheProperty")}
        value={property.name}
        handleChange={(e) => props.setTitle(e)}
        handleOnBlur={onBlurChangeName}
        outerRef={nameInputRef}
        hasError={namehasError}
        height="middle"
      />
    );
  }

  function propertyValue() {
    return (
      <div
        className={clsx(
          "items-center border-l border-gray-100 text-gray-900 w-full",
          { " cursor-not-allowed pointer-events-none": !property.name }
        )}
      >
        {property.property_type === PropertyTypeChoices.Text && (
          <TextArea
            className={clsx(
              "text-gray-900 px-2 py-[8px] border-none rounded-none resize-none",
              "focus:!shadow-[inset_0_0_0_1px] focus:!shadow-indigo-600 focus:!border-none focus:ring-0"
            )}
            placeholder={t("productEditor.empty")}
            value={property.value as string}
            handleChange={(e) => props.setValue(e)}
            type="string"
            height="middle"
          />
        )}
        {property.property_type === PropertyTypeChoices.Number && (
          <Input
            className={clsx(
              "text-gray-900 !px-2 py-[6px] border-none rounded-none",
              "  focus:!shadow-[inset_0_0_0_1px] focus:!shadow-indigo-600 focus:!border-none focus:ring-0"
            )}
            placeholder={t("productEditor.empty")}
            value={property.value as string}
            onChange={(e) => props.setValue(e)}
            noMargin
            type="number"
          />
        )}
        {property.property_type === PropertyTypeChoices.Boolean && (
          <Checkbox
            checked={property.value as boolean}
            onChange={onChangeCheckbox}
            className="ml-2"
            containerClassName="h-full"
          />
        )}
        {property.property_type === PropertyTypeChoices.Multiselect && (
          <MultiSelect property={property} onChange={onChangeSelect} />
        )}
        {property.property_type === PropertyTypeChoices.Select && (
          <Select property={property} onChange={onChangeSelect} />
        )}
      </div>
    );
  }

  const handleClickPropertyDelete = () => {
    property.slug
      ? setOpenConfirmModal(true)
      : props.removeProperty(property.id);
  };

  function actions() {
    return (
      <div
        className={clsx(
          "min-w-9 min-h-4 h-full px-1 py-2 border-l border-gray-100 text-gray-500 flex items-center"
        )}
      >
        <IconTrash
          className="text-gray-700 hover:text-red-700 cursor-pointer ml-1 mr-0"
          stroke={2.5}
          size={20}
          onClick={handleClickPropertyDelete}
        />
      </div>
    );
  }

  useEffect(() => {
    if (propertyManagerStore.lastIndexFocused === props.index) {
      nameInputRef.current?.focus();
      propertyManagerStore.setLastIndexFocused(null);
    }
  }, [propertyManagerStore.lastIndexFocused]);

  return (
    <div
      className="flex border-t border-gray-300 first-of-type:border-t-0 relative group/property"
      key={props.index}
      id={`${id}`}
      style={style}
      ref={setNodeRef}
    >
      <div className="flex-1">
        <div className="flex items-start h-full">
          <div
            className={clsx(
              "min-w-6 min-h-4 h-full px-2 py-2 border-r border-gray-100 relative text-gray-500",
              "flex items-center"
            )}
          >
            {dragHandle}
            <PropertyIcon name={property.property_type} size={20} />
          </div>
          {propertyName()}
        </div>
      </div>
      <div className="flex-1 flex justify-between">
        {propertyValue()}
        {actions()}
      </div>
      <ConfirmModal
        type="warning"
        open={openConfirmModal}
        close={() => setOpenConfirmModal(false)}
        onConfirm={() => props.removeProperty(property.id, property.slug)}
        title={
          <span
            dangerouslySetInnerHTML={{
              __html: t("productEditor.removePropertyTitle"),
            }}
          ></span>
        }
        descriptionMesage={t("productEditor.removePropertyMsg", {
          option_name: property.name,
        })}
        confirmButtonTitle={t("productEditor.removeProperty")}
        confirmButtonType="error"
        cancelTitle={t("commons.cancel")}
      />
    </div>
  );
};

export default observer(PropertyRowEditable);
